/**
 * DatePicker — shadcn Calendar inside a Popover.
 *
 * Drop-in replacement for `<input type="date">` so date selection looks
 * consistent with the rest of the design system instead of using the
 * browser's native widget.
 *
 * Value contract: ISO-8601 date string (YYYY-MM-DD) — matches what the
 * backend stores and what `<input type="date">` already produced. This means
 * existing form state continues to work unchanged when swapping in.
 */
import React from 'react';
import { format, parseISO, isValid } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

const toIso = (d) => (d instanceof Date && !isNaN(d) ? format(d, 'yyyy-MM-dd') : '');
const fromIso = (s) => {
  if (!s) return undefined;
  const d = parseISO(s.length > 10 ? s.slice(0, 10) : s);
  return isValid(d) ? d : undefined;
};

export const DatePicker = ({
  value,
  onChange,
  placeholder = 'Pick a date',
  className,
  disabled,
  'data-testid': testId,
}) => {
  const selected = fromIso(value);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            'w-full h-10 justify-start text-left font-normal bg-input border-border rounded-sm',
            !selected && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {selected ? format(selected, 'd MMM yyyy') : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => onChange(toIso(d))}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};
